import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ErrorBanner, Loading, Spinner } from '../components/ui.js';
import { api, type Organization } from '../lib/api.js';
import { formatDate, titleCase } from '../lib/format.js';
import { useClientConfig } from '../lib/config.js';
import { useAction, useAsync } from '../lib/hooks.js';

/**
 * Organization list, doubling as onboarding.
 *
 * A user with no organizations sees the create form immediately rather than an
 * empty list with a button — creating one is the only sensible next action.
 */
export function OrganizationsPage(): ReactNode {
  const navigate = useNavigate();
  const organizations = useAsync(() => api.get<{ data: Organization[] }>('/api/organizations'), []);
  const [name, setName] = useState('');
  const config = useClientConfig();

  /*
   * Two groups, held apart on purpose.
   *
   * `address` is what the record is for and every field in it is required.
   * `marketing` is asked on RioPlay's behalf, and nothing in it blocks a
   * teacher from finishing. Keeping them in separate objects is what stops a
   * later edit quietly making an optional question compulsory.
   */
  const [address, setAddress] = useState({
    addressLine1: '',
    addressLine2: '',
    city: '',
    region: '',
    postalCode: '',
    country: 'IN',
  });
  const [marketing, setMarketing] = useState({
    subjects: [] as string[],
    subjectsOther: '',
    youtubeHandle: '',
    instagramHandle: '',
    audienceBand: '',
    referralSource: '',
  });
  const [showMarketing, setShowMarketing] = useState(false);

  const { run, pending, error } = useAction();

  if (organizations.loading) return <Loading />;

  const list = organizations.data?.data ?? [];
  const ownsOne = list.some((org) => org.role === 'owner');

  async function create(event: FormEvent): Promise<void> {
    event.preventDefault();
    await run(async () => {
      const created = await api.post<Organization>('/api/organizations', {
        name,
        profile: {
          ...address,
          // Empty strings are not absent values. The API takes null or omission
          // for the optional fields, and '' would be stored as a blank answer.
          addressLine2: address.addressLine2.trim() || null,
          region: address.region.trim() || null,
          subjects: marketing.subjects,
          subjectsOther: marketing.subjectsOther.trim() || null,
          youtubeHandle: marketing.youtubeHandle.trim() || null,
          instagramHandle: marketing.instagramHandle.trim() || null,
          audienceBand: marketing.audienceBand || null,
          referralSource: marketing.referralSource || null,
        },
      });
      setName('');
      void navigate(`/orgs/${created.id}`);
    });
  }

  return (
    <div className={`container${list.length === 0 ? ' container-narrow' : ''}`}>
      <div className="stack">
        {/* Owning is what the allowance counts; being invited to one is not. */}
        <div className="page-head">
          <div>
            <h1>{list.length === 0 ? 'Name your institute' : 'Your institutes'}</h1>
            <p>
              {list.length === 0
                ? 'Your classes, students and join codes all belong to an institute. You will be its owner.'
                : 'Open one to schedule classes and manage students.'}
            </p>
          </div>
        </div>

        <ErrorBanner error={organizations.error} />
        <ErrorBanner message={error} />

        {list.length > 0 && (
          <div className="grid">
            {list.map((org) => (
              <Link
                key={org.id}
                to={`/orgs/${org.id}`}
                className="card"
                style={{ display: 'block' }}
              >
                <div className="row">
                  <h2 style={{ fontSize: '1rem' }}>{org.name}</h2>
                  <span className="badge" style={{ marginLeft: 'auto' }}>
                    {titleCase(org.role)}
                  </span>
                </div>
                <p className="small faint mono" style={{ marginTop: '0.3rem' }}>
                  /{org.slug}
                </p>
                <p className="small muted" style={{ marginTop: '0.5rem' }}>
                  Created {formatDate(org.createdAt)}
                </p>
              </Link>
            ))}
          </div>
        )}

        <section className="card">
          <form onSubmit={(e) => void create(e)} className="stack">
            <div>
              <label htmlFor="org-name">
                {list.length === 0 ? 'Institute name' : 'Start another institute'}
              </label>
              <input
                id="org-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Raja Kelkar Museum"
                required
                maxLength={120}
              />
              <p className="field-hint">
                {ownsOne
                  ? /*
                     * Said before the attempt, not after it. An account may run one
                     * institute, so for almost everyone reading this the form below
                     * is going to be refused — and a refusal that could have been a
                     * sentence is a worse experience than the sentence.
                     */
                    'An account can run one institute. Ask RioPlay support if you genuinely need a second.'
                  : 'The web address is made from this name, so it has to be unique.'}
              </p>
            </div>
            {/* ---------------------------------------------------- where */}

            <div className="grid">
              <div>
                <label htmlFor="org-address1">Address</label>
                <input
                  id="org-address1"
                  value={address.addressLine1}
                  onChange={(e) => setAddress({ ...address, addressLine1: e.target.value })}
                  placeholder="Building, street"
                  required
                  maxLength={200}
                  autoComplete="address-line1"
                />
              </div>
              <div>
                <label htmlFor="org-address2">Area (optional)</label>
                <input
                  id="org-address2"
                  value={address.addressLine2}
                  onChange={(e) => setAddress({ ...address, addressLine2: e.target.value })}
                  maxLength={200}
                  autoComplete="address-line2"
                />
              </div>
              <div>
                <label htmlFor="org-city">City</label>
                <input
                  id="org-city"
                  value={address.city}
                  onChange={(e) => setAddress({ ...address, city: e.target.value })}
                  required
                  maxLength={100}
                  autoComplete="address-level2"
                />
              </div>
              <div>
                <label htmlFor="org-region">State (optional)</label>
                <input
                  id="org-region"
                  value={address.region}
                  onChange={(e) => setAddress({ ...address, region: e.target.value })}
                  maxLength={100}
                  autoComplete="address-level1"
                />
              </div>
              <div>
                <label htmlFor="org-pin">PIN code</label>
                <input
                  id="org-pin"
                  value={address.postalCode}
                  onChange={(e) => setAddress({ ...address, postalCode: e.target.value })}
                  inputMode={address.country === 'IN' ? 'numeric' : 'text'}
                  placeholder={address.country === 'IN' ? '411004' : ''}
                  required
                  maxLength={12}
                  autoComplete="postal-code"
                />
              </div>
            </div>

            {/* ------------------------------------------------- marketing */}

            {/*
              Behind a disclosure, and labelled optional in so many words. These
              questions are asked for RioPlay's benefit rather than the
              teacher's, and someone who wants to schedule a class this evening
              should not have to scroll past them to reach the button.
            */}
            <div>
              <button
                type="button"
                className="button-ghost"
                onClick={() => setShowMarketing(!showMarketing)}
                aria-expanded={showMarketing}
              >
                {showMarketing ? 'Hide' : 'Add'} what you teach (optional)
              </button>
            </div>

            {showMarketing && (
              <div className="stack stack-sm">
                <div>
                  <span className="label-text">Subjects</span>
                  <div className="row" style={{ marginTop: '0.4rem' }}>
                    {config.subjects.map((subject) => {
                      const chosen = marketing.subjects.includes(subject.value);
                      return (
                        <button
                          key={subject.value}
                          type="button"
                          className={chosen ? 'chip chip-on' : 'chip'}
                          aria-pressed={chosen}
                          onClick={() =>
                            setMarketing({
                              ...marketing,
                              subjects: chosen
                                ? marketing.subjects.filter((v) => v !== subject.value)
                                : [...marketing.subjects, subject.value],
                            })
                          }
                        >
                          {subject.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label htmlFor="org-subjects-other">Anything not listed</label>
                  <input
                    id="org-subjects-other"
                    value={marketing.subjectsOther}
                    onChange={(e) => setMarketing({ ...marketing, subjectsOther: e.target.value })}
                    maxLength={200}
                  />
                </div>

                <div className="grid">
                  <div>
                    <label htmlFor="org-youtube">YouTube handle</label>
                    <input
                      id="org-youtube"
                      value={marketing.youtubeHandle}
                      onChange={(e) =>
                        setMarketing({ ...marketing, youtubeHandle: e.target.value })
                      }
                      placeholder="@yourchannel"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label htmlFor="org-instagram">Instagram handle</label>
                    <input
                      id="org-instagram"
                      value={marketing.instagramHandle}
                      onChange={(e) =>
                        setMarketing({ ...marketing, instagramHandle: e.target.value })
                      }
                      placeholder="@yourhandle"
                      maxLength={100}
                    />
                  </div>
                  <div>
                    <label htmlFor="org-audience">Following</label>
                    <select
                      id="org-audience"
                      value={marketing.audienceBand}
                      onChange={(e) => setMarketing({ ...marketing, audienceBand: e.target.value })}
                    >
                      <option value="">Prefer not to say</option>
                      {config.audienceBands.map((band) => (
                        <option key={band.value} value={band.value}>
                          {band.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="org-referral">How you heard about RioPlay</label>
                    <select
                      id="org-referral"
                      value={marketing.referralSource}
                      onChange={(e) =>
                        setMarketing({ ...marketing, referralSource: e.target.value })
                      }
                    >
                      <option value="">Prefer not to say</option>
                      {config.referralSources.map((source) => (
                        <option key={source.value} value={source.value}>
                          {source.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            )}

            <div>
              <button
                type="submit"
                className="button-primary"
                disabled={
                  pending ||
                  !name.trim() ||
                  !address.addressLine1.trim() ||
                  !address.city.trim() ||
                  !address.postalCode.trim()
                }
              >
                {pending && <Spinner />}
                Create institute
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}
